<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useAuthStore } from '@/stores/auth.store';
import { useQuestsStore } from '@/stores/quests.store';
import { extractApiError } from '@/api/client';
import { useAsyncLoad } from '@/composables/useAsyncLoad';
import { fetchMe, type ActiveGameDto } from '@/api/me';
import {
  fetchPlayerQuests,
  fetchAvailableForPlayer,
  acceptQuest,
  cancelQuest,
  completeQuest,
  type PlayerQuestModel,
  type AvailableQuestItem,
} from '@/api/quests';
import { parseRewards } from '@/types/questTemplate';
import ResourceCostList from '@/components/shared/ResourceCostList.vue';
import type { CostLine } from '@/types/constructible';
import { fetchPlayerRank, fetchAllRanks } from '@/api/ranks';
import { fetchMyStats } from '@/api/stats';
import { fetchActiveStatusEffects } from '@/api/statusEffects';
import type { PlayerRankResponse, MilitaryRankModel } from '@/types/ranks';
import type { PlayerStatsView } from '@/types/stats';
import type { ActiveStatusEffectEntry } from '@/types/statusEffects';
import { formatRemaining } from '@/types/statusEffects';

/**
 * Lot 45A + 50J / §33.13 — Journal de quetes + bandeau profil joueur.
 *
 * Le bandeau du haut (avatar / score + grade militaire / status effects)
 * est aligne sur mockup 09. Les blocs Alliance + Medailles sont masques
 * tant que le backend ne fournit pas les donnees (V1 — alliance et medals
 * arriveront avec un Lot dedie).
 */

const QUEST_STATUS = {
  Active: 0,
  Completed: 1,
  Failed: 2,
  Cancelled: 3,
  Expired: 4,
} as const;

const auth = useAuthStore();
const questsStore = useQuestsStore();
const playerUniverseId = ref<number | null>(null);
const activeGame = ref<ActiveGameDto | null>(null);
const active = ref<PlayerQuestModel[]>([]);
const available = ref<AvailableQuestItem[]>([]);
const rank = ref<PlayerRankResponse | null>(null);
const nextRank = ref<MilitaryRankModel | null>(null);
const stats = ref<PlayerStatsView | null>(null);
const statusEffects = ref<ActiveStatusEffectEntry[]>([]);
// useAsyncLoad : loading + toast erreur centralises. Le prefix "Quetes :"
// permet a l utilisateur d identifier la source de l erreur dans le toast.
const { loading, run } = useAsyncLoad({ errorPrefix: 'Quetes :' });
const tab = ref<'active' | 'available'>('active');
const typeFilter = ref<string>('all');
const now = ref(Date.now());

async function load(): Promise<void> {
  await run(async () => {
    if (playerUniverseId.value == null) {
      const me = await fetchMe();
      const game = me.games[0];
      if (!game) {
        ElMessage.warning('Aucune partie active. Rejoignez un univers pour voir les quêtes.');
        return;
      }
      playerUniverseId.value = game.playerUniverseId;
      activeGame.value = game;
    }
    const puId = playerUniverseId.value;
    // 6 endpoints en parallele : actives + dispo + rank + stats + effets +
    // tous les ranks (pour calculer le rang suivant). Les .catch() locaux
    // permettent un degradation gracieuse si un seul endpoint echoue.
    const [act, avail, rk, st, eff, allRanks] = await Promise.all([
      fetchPlayerQuests(puId, QUEST_STATUS.Active),
      fetchAvailableForPlayer(puId),
      fetchPlayerRank(puId).catch(() => null),
      fetchMyStats(puId).catch(() => null),
      fetchActiveStatusEffects('Player', puId).catch(() => []),
      fetchAllRanks().catch(() => []),
    ]);
    active.value = act;
    available.value = avail;
    rank.value = rk;
    stats.value = st;
    statusEffects.value = eff;
    if (rk && allRanks.length) {
      const sorted = [...allRanks].sort((a, b) => a.minCombatScore - b.minCombatScore);
      nextRank.value = sorted.find(r => r.minCombatScore > rk.rank.minCombatScore) ?? null;
    }
  });
}

// Tick 30 s pour rafraîchir les compteurs de temps — nettoyé au démontage
// (évitait un timer fuyant : setInterval sans clearInterval).
let tickTimer: number | undefined;
onMounted(() => {
  void load();
  tickTimer = window.setInterval(() => { now.value = Date.now(); }, 30_000);
});
onUnmounted(() => { if (tickTimer) window.clearInterval(tickTimer); });

// ---- Quetes -----------------------------------------------------------------

function progressPct(q: PlayerQuestModel): number {
  if (q.targetCount <= 0) return 0;
  return Math.min(100, Math.round((q.progress / q.targetCount) * 100));
}

// P2.4 -- compat alignment / faction. Miroir de QuestFactionFormula
// cote backend. Le backend revalide de toute facon.
function isQuestAlignmentCompatible(item: AvailableQuestItem): boolean {
  const faction = item.template.sponsoringFaction;
  if (!faction || faction === 'Player') return true;
  const align = stats.value?.alignmentScore ?? 0;
  if (faction === 'Empire') return align > -50;
  if (faction === 'Bandits') return align < 50;
  return true;
}

function factionTone(faction: 'Player' | 'Empire' | 'Bandits' | undefined):
  'info' | 'success' | 'danger' {
  if (faction === 'Empire') return 'success';
  if (faction === 'Bandits') return 'danger';
  return 'info';
}

function factionLabel(faction: 'Player' | 'Empire' | 'Bandits' | undefined): string {
  if (faction === 'Empire') return 'Empire';
  if (faction === 'Bandits') return 'Bandits';
  return 'Neutre';
}

function progressStatus(q: PlayerQuestModel): '' | 'success' | 'warning' {
  if (progressPct(q) >= 100) return 'success';
  if (q.expiresAt && new Date(q.expiresAt).getTime() - Date.now() < 24 * 3600 * 1000) return 'warning';
  return '';
}

function timeRemaining(iso: string | null): string {
  if (!iso) return 'sans limite';
  const remaining = new Date(iso).getTime() - Date.now();
  if (remaining <= 0) return 'expirée';
  const days = Math.floor(remaining / (24 * 3600 * 1000));
  if (days > 0) return `${days}j`;
  const hours = Math.floor(remaining / (3600 * 1000));
  if (hours > 0) return `${hours}h`;
  return `${Math.floor(remaining / 60000)}m`;
}

// Récompenses ressources (flouz + ressources) en pastilles [icône X], comme les
// coûts de construction (ResourceCostList). Les points = score abstrait à part.
function rewardLines(rewardsJson: string): CostLine[] {
  const r = parseRewards(rewardsJson);
  const lines: CostLine[] = [];
  if (r.flouz) lines.push({ code: 'flouz', label: 'Flouz', amount: r.flouz });
  for (const res of r.resources ?? []) lines.push({ code: res.code, amount: res.qty });
  return lines;
}
function rewardPoints(rewardsJson: string): number {
  return parseRewards(rewardsJson).points ?? 0;
}
function hasRewards(rewardsJson: string): boolean {
  return rewardLines(rewardsJson).length > 0 || rewardPoints(rewardsJson) > 0;
}

function templateOf(q: PlayerQuestModel) {
  return available.value.find(a => a.template.firstStepEventKey === q.eventKey)?.template;
}

function labelOf(q: PlayerQuestModel): string {
  return templateOf(q)?.label ?? `Quête #${q.id}`;
}

function descOf(q: PlayerQuestModel): string {
  return templateOf(q)?.description ?? '';
}

function typeCodeOf(q: PlayerQuestModel): string {
  return templateOf(q)?.questTypeCode ?? 'autres';
}

// ---- Filtre par type --------------------------------------------------------

interface QuestTypeMeta {
  code: string;
  label: string;
  icon: string;
  cssClass: string;
}

const QUEST_TYPES: QuestTypeMeta[] = [
  { code: 'combat',      label: 'Combat',      icon: '⚔️', cssClass: 'qt-combat' },
  { code: 'exploration', label: 'Exploration', icon: '🧭', cssClass: 'qt-explore' },
  { code: 'livraison',   label: 'Livraison',   icon: '📦', cssClass: 'qt-deliv' },
  { code: 'espionnage',  label: 'Espionnage',  icon: '👁️', cssClass: 'qt-spy' },
  { code: 'escorte',     label: 'Escorte',     icon: '🛡️', cssClass: 'qt-escort' },
  { code: 'fouille',     label: 'Fouille',     icon: '⛏️', cssClass: 'qt-fouille' },
  { code: 'diplomatie',  label: 'Diplomatie',  icon: '🤝', cssClass: 'qt-diplo' },
  { code: 'sauvetage',   label: 'Sauvetage',   icon: '🚨', cssClass: 'qt-rescue' },
];

function typeMeta(code: string): QuestTypeMeta {
  return QUEST_TYPES.find(t => t.code === code) ?? {
    code: 'autres', label: 'Autres', icon: '📜', cssClass: 'qt-other',
  };
}

// Catégorie (Tutoriel / Principale / Annexe) : ordre d'affichage + libellé.
// Les quêtes sont triées par catégorie pour les regrouper visuellement.
const CATEGORY_RANK: Record<string, number> = { Tutoriel: 0, Principale: 1, Annexe: 2 };
function catRank(c?: string | null): number { return CATEGORY_RANK[c ?? 'Annexe'] ?? 9; }

const activeFiltered = computed(() => {
  const base = typeFilter.value === 'all'
    ? active.value
    : active.value.filter(q => typeCodeOf(q) === typeFilter.value);
  return [...base].sort((a, b) => catRank(a.category) - catRank(b.category));
});

const availableNotAccepted = computed(() => {
  const activeCodes = new Set(active.value.map(q => q.eventKey));
  return available.value.filter(a => !activeCodes.has(a.template.firstStepEventKey));
});

const availableFiltered = computed(() => {
  const base = typeFilter.value === 'all'
    ? availableNotAccepted.value
    : availableNotAccepted.value.filter(a => a.template.questTypeCode === typeFilter.value);
  return [...base].sort((a, b) => catRank(a.template.category) - catRank(b.template.category));
});

// ---- Actions ----------------------------------------------------------------

async function onAccept(item: AvailableQuestItem): Promise<void> {
  if (playerUniverseId.value == null) return;
  try {
    await acceptQuest({
      playerUniverseId: playerUniverseId.value,
      questCode: item.code,
    });
    ElMessage.success(`Quête "${item.template.label}" acceptée.`);
    tab.value = 'active';
    await load();
  } catch (err) {
    ElMessage.error(extractApiError(err));
  }
}

async function onCancel(q: PlayerQuestModel, label: string): Promise<void> {
  try {
    await ElMessageBox.confirm(
      `Annuler la quête "${label}" ? Les récompenses ne seront pas versées.`,
      'Confirmation',
      { type: 'warning', confirmButtonText: 'Annuler la quête', cancelButtonText: 'Garder' },
    );
  } catch {
    return;
  }
  try {
    await cancelQuest(q.id);
    ElMessage.success('Quête annulée.');
    await load();
  } catch (err) {
    ElMessage.error(extractApiError(err));
  }
}

async function onComplete(q: PlayerQuestModel, label: string): Promise<void> {
  try {
    await completeQuest(q.id);
    ElMessage.success(`Quête "${label}" terminée — récompenses appliquées.`);
    await load();
    // Refresh du badge global pour reflet immediat (sinon poll 90s).
    void questsStore.refresh();
  } catch (err) {
    ElMessage.error(extractApiError(err));
  }
}

// ---- Profil -----------------------------------------------------------------

const userInitials = computed(() => {
  const login = auth.user?.login ?? activeGame.value?.universeName ?? '';
  return login.slice(0, 2).toUpperCase() || '??';
});

const rankProgressPct = computed(() => {
  if (!rank.value || !nextRank.value) return 100;
  const score = rank.value.combatScore;
  const min = rank.value.rank.minCombatScore;
  const next = nextRank.value.minCombatScore;
  if (next <= min) return 100;
  return Math.max(0, Math.min(100, Math.round(((score - min) / (next - min)) * 100)));
});

function fmtNumber(n: number): string {
  return new Intl.NumberFormat('fr-FR').format(n);
}

function effectIcon(code: string): string {
  if (code.includes('bouclier') || code.includes('shield')) return '🛡️';
  if (code.includes('precision') || code.includes('aim')) return '⚡';
  if (code.includes('rage') || code.includes('combat')) return '⚔️';
  if (code.includes('furtif') || code.includes('stealth')) return '👁️';
  if (code.includes('vitesse') || code.includes('speed')) return '💨';
  return '✨';
}
</script>

<template>
  <div v-loading="loading" class="quests-view">
    <!-- ============================================================== -->
    <!-- BANDEAU PROFIL                                                   -->
    <!-- ============================================================== -->
    <section class="profile-banner">
      <div class="profile-identity">
        <div class="profile-avatar">{{ userInitials }}</div>
        <div>
          <div class="profile-name">{{ auth.user?.login ?? 'Joueur' }}</div>
          <div class="text-xs text-muted" v-if="activeGame">
            Race : <span class="text-accent-positive">{{ activeGame.raceLabel }}</span>
          </div>
          <div v-if="rank" class="profile-rank">
            <span class="rank-medal">⭐</span>
            <span class="rank-label">{{ rank.rank.label }}</span>
          </div>
        </div>
      </div>

      <div class="profile-score">
        <div class="profile-cell-label">Score combat</div>
        <div v-if="rank" class="profile-score-row">
          <span class="profile-score-value">{{ fmtNumber(rank.combatScore) }}</span>
          <span v-if="nextRank" class="text-xs text-muted">
            / {{ fmtNumber(nextRank.minCombatScore) }} → {{ nextRank.label }}
          </span>
          <span v-else class="text-xs text-muted">— grade maximal</span>
        </div>
        <div v-else class="text-xs text-muted">Non classé</div>
        <div class="rank-progress" v-if="rank && nextRank">
          <div class="rank-progress-bar" :style="{ width: rankProgressPct + '%' }" />
        </div>
        <div v-if="rank" class="text-xs text-muted profile-rank-bonus">
          ATK navires : +{{ rank.atkShipPct }}% · DEF : +{{ rank.defShipPct }}%
        </div>
      </div>

      <div class="profile-totals" v-if="stats">
        <div class="profile-cell-label">Activité globale</div>
        <ul class="profile-totals-list">
          <li>
            <span class="text-muted">Planètes</span>
            <span class="text-mono">{{ stats.planetCount }}</span>
          </li>
          <li>
            <span class="text-muted">Flottes</span>
            <span class="text-mono">{{ stats.fleetCount }}</span>
          </li>
          <li>
            <span class="text-muted">Recherches</span>
            <span class="text-mono">{{ stats.researchCompletedCount }}</span>
          </li>
          <li>
            <span class="text-muted">Batailles</span>
            <span class="text-mono">{{ stats.battlesParticipatedCount }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- ============================================================== -->
    <!-- JOURNAL DE QUÊTES                                                -->
    <!-- ============================================================== -->
    <section class="quest-journal">
      <header class="quest-journal-header">
        <div>
          <h2 class="quest-journal-title">📜 Journal de quêtes</h2>
          <p class="text-xs text-muted">
            {{ active.length }} en cours · {{ availableNotAccepted.length }} disponible(s)
          </p>
        </div>
        <div class="quest-filters">
          <button
            class="quest-filter"
            :class="{ active: typeFilter === 'all' }"
            @click="typeFilter = 'all'"
          >
            Toutes
          </button>
          <button
            v-for="t in QUEST_TYPES"
            :key="t.code"
            class="quest-filter"
            :class="{ active: typeFilter === t.code }"
            @click="typeFilter = t.code"
          >
            {{ t.icon }} {{ t.label }}
          </button>
        </div>
      </header>

      <el-tabs v-model="tab" class="quests-tabs">
        <el-tab-pane :label="`En cours (${activeFiltered.length})`" name="active">
          <div v-if="!activeFiltered.length" class="quests-empty">
            <p class="text-muted">Aucune quête active dans cette catégorie.</p>
          </div>

          <div v-else class="quest-list">
            <article
              v-for="q in activeFiltered"
              :key="q.id"
              class="quest-row"
            >
              <div class="quest-icon" :class="typeMeta(typeCodeOf(q)).cssClass">
                {{ typeMeta(typeCodeOf(q)).icon }}
              </div>

              <div class="quest-row-body">
                <div class="quest-row-head">
                  <span class="quest-row-title">{{ labelOf(q) }}</span>
                  <span class="quest-cat-badge" :class="`qcat-${(q.category ?? 'Annexe').toLowerCase()}`">
                    {{ q.category ?? 'Annexe' }}
                  </span>
                  <span class="quest-badge" :class="typeMeta(typeCodeOf(q)).cssClass">
                    {{ typeMeta(typeCodeOf(q)).label.toUpperCase() }}
                  </span>
                  <span class="quest-deadline" :class="{ urgent: progressStatus(q) === 'warning' }">
                    {{ timeRemaining(q.expiresAt) }}
                  </span>
                </div>

                <p v-if="descOf(q)" class="quest-row-desc">{{ descOf(q) }}</p>

                <div class="quest-row-meta">
                  <div class="quest-row-progress" v-if="q.targetCount > 1">
                    <div
                      class="quest-row-progress-bar"
                      :class="typeMeta(typeCodeOf(q)).cssClass"
                      :style="{ width: progressPct(q) + '%' }"
                    />
                  </div>
                  <span class="text-mono quest-row-counter">
                    {{ q.progress }} / {{ q.targetCount }}
                  </span>
                  <span v-if="hasRewards(q.rewardsJson)" class="quest-row-reward">
                    🎁
                    <ResourceCostList v-if="rewardLines(q.rewardsJson).length" :lines="rewardLines(q.rewardsJson)" compact />
                    <span v-if="rewardPoints(q.rewardsJson)" class="text-mono quest-reward-pts">{{ rewardPoints(q.rewardsJson) }} pts</span>
                  </span>
                </div>
              </div>

              <div class="quest-row-actions">
                <el-button
                  v-if="progressPct(q) >= 100"
                  size="small"
                  type="success"
                  @click="onComplete(q, labelOf(q))"
                >
                  Récupérer
                </el-button>
                <el-button
                  v-else
                  size="small"
                  plain
                  @click="onCancel(q, labelOf(q))"
                >
                  Annuler
                </el-button>
              </div>
            </article>
          </div>
        </el-tab-pane>

        <el-tab-pane :label="`Disponibles (${availableFiltered.length})`" name="available">
          <div v-if="!availableFiltered.length" class="quests-empty">
            <p class="text-muted">Aucune quête disponible dans cette catégorie.</p>
          </div>

          <div v-else class="quest-list">
            <article
              v-for="item in availableFiltered"
              :key="item.code"
              class="quest-row"
            >
              <div class="quest-icon" :class="typeMeta(item.template.questTypeCode).cssClass">
                {{ typeMeta(item.template.questTypeCode).icon }}
              </div>

              <div class="quest-row-body">
                <div class="quest-row-head">
                  <span class="quest-row-title">{{ item.template.label }}</span>
                  <span class="quest-badge" :class="typeMeta(item.template.questTypeCode).cssClass">
                    {{ typeMeta(item.template.questTypeCode).label.toUpperCase() }}
                  </span>
                  <span v-if="item.template.difficulty" class="quest-deadline">
                    ★ {{ item.template.difficulty }}/5
                  </span>
                  <!-- P2.4 -- badge faction sponsoring. Toujours visible pour
                       que le joueur sache d'ou vient la quete. -->
                  <el-tag
                    v-if="item.template.sponsoringFaction && item.template.sponsoringFaction !== 'Player'"
                    :type="factionTone(item.template.sponsoringFaction)"
                    size="small"
                    effect="dark"
                  >
                    {{ factionLabel(item.template.sponsoringFaction) }}
                  </el-tag>
                </div>

                <p v-if="item.template.description" class="quest-row-desc">
                  {{ item.template.description }}
                </p>

                <div class="quest-row-meta">
                  <span class="text-mono quest-row-counter">
                    Objectif : {{ item.template.firstStepEventKey }} × {{ item.template.firstStepTargetCount }}
                  </span>
                  <span v-if="item.template.durationDays" class="text-xs text-muted">
                    · {{ item.template.durationDays }} jour(s)
                  </span>
                  <span v-if="hasRewards(item.template.rewardsJson)" class="quest-row-reward">
                    🎁
                    <ResourceCostList v-if="rewardLines(item.template.rewardsJson).length" :lines="rewardLines(item.template.rewardsJson)" compact />
                    <span v-if="rewardPoints(item.template.rewardsJson)" class="text-mono quest-reward-pts">{{ rewardPoints(item.template.rewardsJson) }} pts</span>
                  </span>
                </div>
              </div>

              <div class="quest-row-actions">
                <AppTooltip
                  v-if="!isQuestAlignmentCompatible(item)"
                  effect="dark"
                  placement="top"
                  :content="`Alignment ${stats?.alignmentScore ?? 0} incompatible avec la faction ${factionLabel(item.template.sponsoringFaction)}.`"
                >
                  <el-button type="primary" size="small" disabled>
                    Accepter
                  </el-button>
                </AppTooltip>
                <el-button v-else type="primary" size="small" @click="onAccept(item)">
                  Accepter
                </el-button>
              </div>
            </article>
          </div>
        </el-tab-pane>
      </el-tabs>
    </section>

    <!-- ============================================================== -->
    <!-- STATUS EFFECTS ACTIFS                                            -->
    <!-- ============================================================== -->
    <section v-if="statusEffects.length" class="status-effects-panel">
      <h3 class="status-effects-title">⚡ Status effects actifs</h3>
      <div class="status-effects-grid">
        <div
          v-for="entry in statusEffects"
          :key="entry.model.id"
          class="status-effect-card"
          :class="entry.statusEffect.isBuff ? 'is-buff' : 'is-debuff'"
        >
          <div class="status-effect-icon">{{ effectIcon(entry.statusEffect.code) }}</div>
          <div class="status-effect-label">{{ entry.statusEffect.label }}</div>
          <div class="status-effect-remaining">
            {{ formatRemaining(entry.model.endsAt, now) }}
          </div>
        </div>
      </div>
      <p class="text-xs text-muted status-effects-foot">
        Wiki §33.10 — 15 effets dans le catalogue
      </p>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.quests-view {
  max-width: $layout-max-width;
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

// ---- Bandeau profil ---------------------------------------------------------

.profile-banner {
  @include card-dark;
  padding: $space-5;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: $space-5;
  align-items: center;
}

.profile-identity {
  display: flex;
  align-items: center;
  gap: $space-3;
}

.profile-avatar {
  width: 64px;
  height: 64px;
  border-radius: $radius-full;
  background: $accent-primary;
  color: $color-text-0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: $font-display;
  font-size: $font-size-xl;
  font-weight: $font-weight-bold;
}

.profile-name {
  font-family: $font-display;
  font-size: $font-size-lg;
  font-weight: $font-weight-bold;
}

.profile-rank {
  display: inline-flex;
  align-items: center;
  gap: $space-1;
  margin-top: $space-1;
  font-size: $font-size-xs;
}

.rank-medal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: $radius-full;
  background: linear-gradient(135deg, $color-warning, #d97706);
}

.rank-label {
  font-weight: $font-weight-bold;
}

.profile-cell-label {
  font-size: 10px;
  color: $text-muted;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: $space-1;
}

.profile-score-row {
  display: flex;
  align-items: baseline;
  gap: $space-2;
}

.profile-score-value {
  font-family: $font-mono;
  font-size: $font-size-xl;
  font-weight: $font-weight-bold;
  color: $color-warning;
}

.rank-progress {
  height: 6px;
  border-radius: $radius-full;
  background: $bg-tertiary;
  margin-top: $space-1;
  overflow: hidden;
}

.rank-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, $color-warning, $accent-primary);
  transition: width 0.3s ease;
}

.profile-rank-bonus {
  margin-top: $space-2;
}

.profile-totals-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $space-1 $space-3;

  li {
    display: flex;
    justify-content: space-between;
    font-size: $font-size-xs;
  }
}

// ---- Journal de quetes ------------------------------------------------------

.quest-journal {
  @include card-dark;
  padding: $space-4;
}

.quest-journal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $space-3;
  flex-wrap: wrap;
  margin-bottom: $space-3;
}

.quest-journal-title {
  @include section-title;
  margin: 0 0 $space-1;
}

.quest-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.quest-filter {
  @include button-reset;
  padding: 4px $space-2;
  border-radius: $radius-sm;
  background: $bg-tertiary;
  color: $text-secondary;
  font-size: $font-size-xs;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: $border-strong;
    color: $text-primary;
  }

  &.active {
    background: $accent-primary;
    color: $color-text-0;
  }
}

.quests-tabs {
  margin-top: $space-2;
}

.quests-empty {
  padding: $space-6;
  text-align: center;
  border: 1px dashed $border-default;
  border-radius: $radius-md;
  background: $bg-secondary;
}

.quest-list {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.quest-row {
  display: flex;
  gap: $space-3;
  padding: $space-3;
  border-radius: $radius-md;
  background: $bg-tertiary;
  align-items: flex-start;
}

.quest-icon {
  width: 40px;
  height: 40px;
  border-radius: $radius-full;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: $font-size-lg;
  flex-shrink: 0;
  border: 2px solid transparent;
}

// Couleurs par type — calques sur les tokens, intensite stable
.qt-combat   { background: rgba($color-danger, 0.15); color: $color-danger;     border-color: $color-danger; }
.qt-explore  { background: rgba(96, 165, 250, 0.15); color: $color-accent-400; border-color: $color-accent-400; }
.qt-deliv    { background: rgba(125, 211, 252, 0.15); color: $color-neon-cyan;  border-color: $color-neon-cyan; }
.qt-spy      { background: rgba(167, 139, 250, 0.15); color: $color-neon-violet; border-color: $color-neon-violet; }
.qt-escort   { background: rgba($color-success, 0.15); color: $color-success;    border-color: $color-success; }
.qt-fouille  { background: rgba($color-warning, 0.15); color: $color-warning;    border-color: $color-warning; }
.qt-diplo    { background: rgba(244, 114, 182, 0.15); color: $color-neon-pink; border-color: $color-neon-pink; }
.qt-rescue   { background: rgba($color-danger, 0.15); color: $color-danger;     border-color: $color-danger; }
.qt-other    { background: $bg-secondary;                                       border-color: $border-default; }

// Variante pour la barre de progression : utilise la meme couleur que l'icone
.quest-row-progress-bar.qt-combat   { background: $color-danger; }
.quest-row-progress-bar.qt-explore  { background: $color-accent-400; }
.quest-row-progress-bar.qt-deliv    { background: $color-neon-cyan; }
.quest-row-progress-bar.qt-spy      { background: $color-neon-violet; }
.quest-row-progress-bar.qt-escort   { background: $color-success; }
.quest-row-progress-bar.qt-fouille  { background: $color-warning; }
.quest-row-progress-bar.qt-diplo    { background: $color-neon-pink; }
.quest-row-progress-bar.qt-rescue   { background: $color-danger; }
.quest-row-progress-bar.qt-other    { background: $accent-primary; }

.quest-row-body {
  flex: 1;
  min-width: 0;
}

.quest-row-head {
  display: flex;
  align-items: center;
  gap: $space-2;
  flex-wrap: wrap;
}

.quest-row-title {
  font-weight: $font-weight-bold;
}

.quest-badge {
  padding: 2px 6px;
  border-radius: $radius-sm;
  font-size: 10px;
  font-weight: $font-weight-semibold;
  letter-spacing: 0.05em;
}

// Badge de categorie (Tutoriel / Principale / Annexe) -- 3 familles de quetes.
.quest-cat-badge {
  padding: 2px 6px;
  border-radius: $radius-sm;
  font-size: 10px;
  font-weight: $font-weight-semibold;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: 1px solid $border-subtle;
  color: $text-muted;

  &.qcat-tutoriel   { color: $color-neon-cyan;   border-color: rgba($color-neon-cyan, 0.45); }
  &.qcat-principale { color: $accent-positive;   border-color: rgba($accent-positive, 0.45); }
  &.qcat-annexe     { color: $color-warning;     border-color: rgba($color-warning, 0.45); }
}

.quest-deadline {
  margin-left: auto;
  font-size: $font-size-xs;
  color: $text-muted;
  padding: 2px 6px;
  border-radius: $radius-sm;
  background: $bg-secondary;

  &.urgent {
    color: $color-warning;
  }
}

.quest-row-desc {
  font-size: $font-size-xs;
  color: $text-secondary;
  margin: $space-1 0 0;
  line-height: 1.4;
}

.quest-row-meta {
  display: flex;
  align-items: center;
  gap: $space-2;
  margin-top: $space-2;
  font-size: $font-size-xs;
  flex-wrap: wrap;
}

.quest-row-progress {
  flex: 1;
  max-width: 240px;
  height: 6px;
  border-radius: $radius-full;
  background: $bg-secondary;
  overflow: hidden;
}

.quest-row-progress-bar {
  height: 100%;
  background: $accent-primary;
  transition: width 0.3s ease;
}

.quest-row-counter {
  color: $text-secondary;
}

.quest-row-reward {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $color-warning;
  font-size: $font-size-xs;
}
.quest-reward-pts { color: $color-warning; }

.quest-row-actions {
  flex-shrink: 0;
  align-self: flex-start;
}

// ---- Status effects ---------------------------------------------------------

.status-effects-panel {
  @include card-dark;
  padding: $space-4;
}

.status-effects-title {
  font-family: $font-display;
  font-size: $font-size-md;
  margin: 0 0 $space-3;
}

.status-effects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: $space-2;
}

.status-effect-card {
  padding: $space-3;
  border-radius: $radius-md;
  text-align: center;
  border: 1px solid $border-default;

  &.is-buff {
    background: rgba($color-success, 0.10);
    border-color: $color-success;
  }

  &.is-debuff {
    background: rgba($color-danger, 0.10);
    border-color: $color-danger;
  }
}

.status-effect-icon {
  font-size: $font-size-2xl;
}

.status-effect-label {
  font-size: $font-size-xs;
  font-weight: $font-weight-semibold;
  margin-top: $space-1;
}

.status-effect-remaining {
  font-size: 10px;
  color: $text-muted;
  margin-top: 2px;
}

.status-effects-foot {
  margin-top: $space-3;
}

// ---- Utilitaires d'ecran etroit --------------------------------------------

.text-xs { font-size: $font-size-xs; }
.text-mono { font-family: $font-mono; }
.text-muted { color: $text-muted; }
.text-accent-positive { color: $color-success; }
</style>
